import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { usePermissions, useMetric, useAuthContext, useModal, useManageSubscriptionPath } from '/src/hooks/';
import { inflect } from '/src/utils/string';
import { featureUsage } from '/src/utils/features';
import { getDaysRemainingInMonthServerTime } from '/src/utils/date';
import { DashboardBanner } from '/src/components/DashboardBanner';
import { FEATURES, SUBSCRIPTION_STATES, CAPABILITY_REASONS } from '../constants';
import { EnterprisePlanModal } from '/src/features/account/components/modals/EnterprisePlanModal';
import { AppLink } from '/src/features/_global/components/AppLink';

export function RecordingAlerts() {
  const { currentAccount, currentUser, capabilities, subscription } = useAuthContext();
  const isStripeUser = Boolean(currentAccount?.useStripe);
  const trackMetric = useMetric();
  const navigate = useNavigate();
  const modal = useModal();
  const permissions = usePermissions();
  const manageSubscriptionPath = useManageSubscriptionPath();

  const { allowed: canChangePlan, reason } = permissions.can('changePlan', FEATURES.BILLING);
  const showEnterpriseModal = !canChangePlan && reason === CAPABILITY_REASONS.CONTACT_SUPPORT;

  const [hasDismissedAlerts, setHasDismissedAlerts] = useState(
    Boolean(window.sessionStorage.getItem('ce.dashboards.recordings.dismissedAlerts') ?? false),
  );

  const canManageSubscription = permissions.can('edit', FEATURES.BILLING).allowed;
  const canEditRecordings = permissions.can('edit', FEATURES.RECORDINGS).allowed;

  const dontShowPaymentAlert = [181, 197].includes(currentUser.subscriptions.recordings.paymentPlan.id); // ch18817,sc24366
  const isExpiring = subscription.state === SUBSCRIPTION_STATES.EXPIRING;

  function dismissAlerts() {
    window.sessionStorage.setItem('ce.dashboards.recordings.dismissedAlerts', 'true');
    setHasDismissedAlerts(true);
  }

  if (!canEditRecordings) {
    return null;
  }

  const daysRemaining = getDaysRemainingInMonthServerTime();

  if (isExpiring && !dontShowPaymentAlert && canManageSubscription) {
    if (hasDismissedAlerts) return null;

    return (
      <DashboardBanner title="There’s a payment issue!" data-testid="alert-banner" onClose={dismissAlerts}>
        Your Account will be deactivated soon due to a problem with payment.{' '}
        <AppLink
          to="/account/billing?billingModal=true"
          className="text-link !text-white !underline"
          target={isStripeUser ? '_blank' : '_self'}
          rel={isStripeUser ? 'noopener noreferrer' : undefined}
        >
          Please update your payment info
        </AppLink>
        .
      </DashboardBanner>
    );
  }

  const { monthlyRecordings } = capabilities.recordings.quotas;
  const canStoreMore = monthlyRecordings.current < monthlyRecordings.limit;
  const recordingsUsage = featureUsage(capabilities.recordings.quotas.monthlyRecordings);

  if (!canStoreMore) {
    return (
      <DashboardBanner title="Recordings Limit Reached!" data-testid="alert-banner">
        You’ve used up 100% of the recordings quota this month.{' '}
        {canManageSubscription ? (
          <button
            type="button"
            onClick={() => {
              trackMetric('upgrade_cta_click', 'recordings_pageview_limit_reached_banner');
              if (showEnterpriseModal) {
                modal.show(<EnterprisePlanModal />);
              } else {
                navigate(manageSubscriptionPath);
              }
            }}
            className="text-link !text-white !underline"
          >
            Upgrade now
          </button>
        ) : (
          <>
            Please contact the account owner{' '}
            <a href={`mailto:${currentAccount.ownerEmail}`} className="text-link !font-semibold !text-white !underline">
              {currentAccount.ownerEmail}
            </a>
          </>
        )}{' '}
        or wait {daysRemaining} {inflect('day', daysRemaining)} to continue recording more sessions.
      </DashboardBanner>
    );
  }

  if (recordingsUsage > 90 && canStoreMore) {
    if (hasDismissedAlerts) return null;
    return (
      <DashboardBanner data-testid="alert-banner" onClose={dismissAlerts}>
        You’ve reached {recordingsUsage}% of your recordings quota this month. When you reach 100%, sessions will stop
        tracking.
        {canManageSubscription ? (
          <button
            type="button"
            onClick={() => {
              trackMetric('upgrade_cta_click', 'recordings_pageview_limit_reached_banner');
              if (showEnterpriseModal) {
                modal.show(<EnterprisePlanModal />);
              } else {
                navigate(manageSubscriptionPath);
              }
            }}
            className="text-link !text-white !underline"
          >
            Upgrade now
          </button>
        ) : (
          <>
            Please contact the account owner{' '}
            <a href={`mailto:${currentAccount.ownerEmail}`} className="text-link">
              {currentAccount.ownerEmail}
            </a>
          </>
        )}
        .
      </DashboardBanner>
    );
  }

  return null;
}
